import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import apiClient from '../../services/api/apiClient';
import { useToast } from '../../components/ui';
import OnboardingLayout from '../../components/onboarding/OnboardingLayout';

function Step6DecisionScope() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const editAccountId = searchParams.get('edit');
  const isEditMode = !!editAccountId;

  const [selectedScope, setSelectedScope] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode && editAccountId) {
      loadAccountSettings(editAccountId);
    }
  }, [isEditMode, editAccountId]);

  const loadAccountSettings = async (accountId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/user/social-accounts/account-settings/id/${accountId}`);
      if (response.data.success && response.data.data.settings?.decision_scope) {
        setSelectedScope(response.data.data.settings.decision_scope);
      }
    } catch (error) {
      console.error('載入設定失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const scopes = [
    {
      id: 'full_control',
      title: '完全掌控',
      description: 'AI 提供建議，所有內容都需要你審核後才發布',
      icon: 'mdi:account-check-outline',
      features: ['AI 生成草稿', '人工審核', '手動排程', '完全控制'],
      automation: '0%'
    },
    {
      id: 'semi_auto',
      title: '半自動化',
      description: 'AI 自動生成內容，你可以編輯修改後發布',
      icon: 'mdi:account-cog-outline',
      features: ['AI 自動生成', '可編輯修改', '自動排程', '定期檢視'],
      automation: '50%'
    },
    {
      id: 'full_auto',
      title: '全自動化',
      description: 'AI 完全自主生成與發布，你只需要查看報表',
      icon: 'mdi:robot-outline',
      features: ['完全自動化', 'AI 自主決策', '自動發布', '每週報表'],
      automation: '100%'
    }
  ];

  const handleComplete = async () => {
    if (!selectedScope) return;

    setIsSubmitting(true);

    try {
      // Collect all onboarding data
      const onboardingData = {
        purposes: JSON.parse(localStorage.getItem('onboarding_purposes') || '[]'),
        tone_styles: JSON.parse(localStorage.getItem('onboarding_tone_styles') || '[]'),
        excluded_theme: localStorage.getItem('onboarding_excluded_theme'),
        target_theme: localStorage.getItem('onboarding_target_theme'),
        reference_accounts: JSON.parse(localStorage.getItem('onboarding_reference_accounts') || '[]'),
        decision_scope: selectedScope
      };

      if (isEditMode && editAccountId) {
        // 編輯模式：更新帳號設定
        await apiClient.put(`/user/social-accounts/account-settings/id/${editAccountId}`, {
          settings: onboardingData
        });

        toast.success('帳號設定已更新');

        // 清除暫存資料
        localStorage.removeItem('onboarding_purposes');
        localStorage.removeItem('onboarding_tone_styles');
        localStorage.removeItem('onboarding_excluded_theme');
        localStorage.removeItem('onboarding_target_theme');
        localStorage.removeItem('onboarding_reference_accounts');
        localStorage.removeItem('editing_account_id');

        // 導航到 My Automations
        navigate('/my-automations');
      } else {
        // 新用戶模式：保存到 user onboarding preferences
        await apiClient.post('/user/onboarding/complete', onboardingData);

        // 清除所有 onboarding 暫存資料
        localStorage.removeItem('onboarding_purposes');
        localStorage.removeItem('onboarding_tone_styles');
        localStorage.removeItem('onboarding_excluded_theme');
        localStorage.removeItem('onboarding_target_theme');
        localStorage.removeItem('onboarding_reference_accounts');
        localStorage.removeItem('onboarding_theme_analysis');
        localStorage.removeItem('onboarding_pending_analysis');
        localStorage.removeItem('onboarding_account_id');
        localStorage.removeItem('onboarding_step1_completed');
        localStorage.removeItem('onboarding_step');

        // 導航到首頁
        navigate('/home');
      }
    } catch (error) {
      console.error('完成設定時發生錯誤:', error);
      toast.error('保存失敗，請稍後再試');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (isEditMode && editAccountId) {
      navigate(`/onboarding/step5?edit=${editAccountId}`);
    } else {
      navigate('/onboarding/step5');
    }
  };

  const illustration = (
    <div className="w-full max-w-md mx-auto">
      <div className="relative flex items-center justify-center">
        {/* Automation level visualization */}
        <div className="relative">
          {/* Main container */}
          <div className="w-64 h-64 rounded-full bg-white/60 backdrop-blur-sm shadow-lg flex items-center justify-center">
            {/* Inner automation bars */}
            <div className="relative w-48 h-48 flex flex-col items-center justify-center gap-3">
              {/* Bar 1 - Full */}
              <div className="w-full h-4 rounded-full bg-gradient-to-r from-blue-400 to-blue-500 shadow-md"></div>
              {/* Bar 2 - 50% */}
              <div className="w-full h-4 rounded-full bg-gray-200 shadow-md relative overflow-hidden">
                <div className="absolute left-0 top-0 h-full w-1/2 bg-gradient-to-r from-blue-400 to-blue-500"></div>
              </div>
              {/* Bar 3 - Empty */}
              <div className="w-full h-4 rounded-full bg-gray-200 shadow-md"></div>

              {/* Robot icon in center */}
              <div className="mt-4 w-20 h-20 rounded-2xl bg-blue-500 shadow-lg flex items-center justify-center">
                <Icon icon="mdi:robot-outline" className="text-4xl text-white" />
              </div>
            </div>
          </div>

          {/* Toggle indicator */}
          <div className="absolute -top-6 right-8 w-16 h-8 rounded-full bg-white shadow-lg flex items-center px-1">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 shadow-md ml-auto"></div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <OnboardingLayout
      currentStep={6}
      totalSteps={6}
      title="你希望 AI 的參與程度？"
      illustration={illustration}
      onBack={handleBack}
      progressColor="#60A5FA"
    >
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <Icon icon="mdi:information-outline" className="text-blue-500 text-xl flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-medium mb-1">自動化程度</p>
            <p className="text-gray-600">
              選擇 AI 在內容創作與發布流程中的角色。你可以隨時在設定中調整此選項。
            </p>
          </div>
        </div>
      </div>

      {/* Instruction Text */}
      <p className="text-sm text-gray-500 text-center mb-6">
        選擇 1 個自動化程度
      </p>

      {/* Scope Options */}
      <div className="space-y-4 mb-6">
        {scopes.map((scope) => {
          const isSelected = selectedScope === scope.id;

          return (
            <button
              key={scope.id}
              onClick={() => setSelectedScope(scope.id)}
              className={`
                w-full p-6 rounded-xl border-2 transition-all text-left
                ${isSelected
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }
              `}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className={`
                  w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                  ${isSelected ? 'bg-blue-500' : 'bg-gray-100'}
                `}>
                  <Icon
                    icon={scope.icon}
                    className={`text-2xl ${isSelected ? 'text-white' : 'text-gray-600'}`}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {scope.title}
                    </h3>
                    <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                      {scope.automation}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    {scope.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {scope.features.map((feature, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs font-medium rounded-md bg-white border border-gray-200 text-gray-600"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                {isSelected && (
                  <Icon icon="mdi:check-circle" className="text-2xl text-blue-500 flex-shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Complete Button */}
      <div className="flex justify-end mt-8">
        <button
          onClick={handleComplete}
          disabled={!selectedScope || isSubmitting}
          className={`
            px-8 py-3 font-medium rounded-xl transition-colors flex items-center gap-2
            ${selectedScope && !isSubmitting
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          {isSubmitting ? (
            <>
              <Icon icon="line-md:loading-loop" className="text-xl" />
              完成設定中...
            </>
          ) : (
            <>
              完成設定
              <Icon icon="mdi:check" className="text-xl" />
            </>
          )}
        </button>
      </div>
    </OnboardingLayout>
  );
}

export default Step6DecisionScope;
